import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import CommonService from 'api/common.service';
import { get } from 'lodash';
import { AppThunk } from '../store';

interface Error {
  message: string;
  code: string | number;
}

interface IRecentItem {
  icon: string;
  title: string;
  path: string;
}

interface EventPaload {
  code: string;
  type?: string;
  data?: any;
}

interface AppConfig {
  locales: any;
  provinces: any;

  parcel_type_master: any;
  shipment_master: any;
  a_service_list: any;
  m_service_list: any;
  partner_list: any;
  config_map: {
    [K: string]: {
      value: any;
    };
  };
}

interface UserConfig {
  code: string;
  atrr?: any;
  atrr2?: any;
  atrr3?: any;
  atrr4?: any;
  atrr5?: any;
  type: any;
  field1str?: string;
  field2int?: number;
}

export interface AppState {
  error: Error | null;
  theme: string;
  language: string;
  loading: boolean;
  exchangeRate?: number;
  navLockStatus: boolean;
  recentList: IRecentItem[];
  favoriteList: IRecentItem[];
  appConfig: AppConfig;
  userConfig: UserConfig;
  eventPayload?: EventPaload;
}

const initialState: AppState = {
  error: null,
  theme: 'default',
  language: 'vi',
  loading: false,
  navLockStatus: false,
  recentList: [],
  favoriteList: [],
  appConfig: {} as AppConfig,
  userConfig: {} as UserConfig,
};

const appSlices = createSlice({
  name: 'app',
  initialState: initialState,
  reducers: {
    displayErrors(state: AppState, { payload }: PayloadAction<Error>) {
      state.error = payload;
      state.loading = false;
    },
    setLoading(state: AppState, { payload }: PayloadAction<boolean>) {
      state.loading = payload || false;
    },
    emitEvent(state: AppState, { payload }: PayloadAction<EventPaload>) {
      if (payload.type === 'RESET') {
        state.eventPayload = null;
      } else {
        state.eventPayload = payload;
      }
    },
    toggleNavDrawer(state: AppState, { payload }: PayloadAction<boolean>) {
      state.navLockStatus = payload;
    },
    updateAppConfig(state: AppState, { payload }: PayloadAction<AppConfig>) {
      state.appConfig = payload;
    },
    updateExchange(state: AppState, { payload }: PayloadAction<number>) {
      state.exchangeRate = payload;
    },
    updateUserConfig(state: AppState, { payload }: PayloadAction<UserConfig>) {
      state.userConfig = payload;
    },
    addNewRecent(state: AppState, { payload }: PayloadAction<IRecentItem>) {
      if (state.recentList.some((v) => v.path === payload.path)) return;
      state.recentList = [payload, ...state.recentList];
      if (state.recentList.length > 15) {
        state.recentList = state.recentList.splice(0, 15);
      }
    },
    addFavorite(state: AppState, { payload }: PayloadAction<IRecentItem>) {
      if (state.favoriteList.some((v) => v.path === payload.path)) return;
      state.favoriteList.push(payload);
    },
    removeFavorite(state: AppState, { payload }: PayloadAction<number>) {
      state.favoriteList = state.favoriteList.filter(
        (_, index) => index === payload
      );
    },
    clearAppData(state: AppState) {
      state.favoriteList = [];
      state.recentList = [];
    },
  },
});

export const {
  displayErrors,
  setLoading,
  clearAppData,
  addNewRecent,
  addFavorite,
  toggleNavDrawer,
  updateAppConfig,
  updateUserConfig,
  removeFavorite,
  updateExchange,
  emitEvent,
} = appSlices.actions;

export const doDisplayErrors =
  (payload: Error): AppThunk =>
  async (dispatch) => {
    dispatch(displayErrors(payload));
  };

export const displayAppLoading =
  (payload: boolean): AppThunk =>
  async (dispatch) => {
    dispatch(setLoading(payload));
  };

export const getAppConfig = (): AppThunk => async (dispatch) => {
  const response = await CommonService.getAppConfig();
  const data = get(response, 'data.data', {});
  dispatch(updateAppConfig(data));
};

export default appSlices.reducer;
